import { useMemo, useState } from 'react';
import { api } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/PageHeader';
import Card from '../components/Card';
import StatCard from '../components/StatCard';
import UsagePivot from '../components/UsagePivot';
import LineChart from '../components/charts/LineChart';
import StackedBars from '../components/charts/StackedBars';
import { Loading, ErrorState, EmptyState } from '../components/States';
import Icon from '../components/Icon';
import { currencyCompact, currencyExact, titleCase } from '../lib/format';
import { monthlyCharts } from '../lib/monthly';
import { downloadCsv, lineItemsCsv } from '../lib/pivot';
import { monthTick, monthFull, dayTick, dayFull, dayRange } from '../lib/timeAxis';
import { ICONS } from '../lib/icons';
import { daysAgoUTC, formatUTCDate, todayUTC } from '../lib/utcDate';

// The client-facing billing view. Everything here is read from the
// database: a superadmin pulls usage from OCI on the Cost & Billing page and
// stores it, and this page shows what was stored for the groups the signed-in
// user belongs to. No provider credentials or API calls are involved.

// Computed at mount rather than module load: a tab left open overnight would
// otherwise still default to the day the bundle was parsed.
const DEFAULT_PERIOD_DAYS = 30;
const defaultStart = () => daysAgoUTC(DEFAULT_PERIOD_DAYS);
const todayISO = todayUTC;
const fmtDate = formatUTCDate;

// Four is what the chart palette can keep telling apart; the rest of the services
// fold into one "Other" column segment rather than being dropped, so the columns
// still stack to each month's true total.
const TOP_SERVICES = 4;
const BILLING_FIRST_MONTH = '2026-08';

// How each contract type reads on a bill. `drawdown` marks the types the
// group's billing consumes — a prepaid Credit is spent down, a Commitment is
// worked off — which are the only ones where a percentage and a balance mean
// anything. The rest carry a value and a term and nothing more.
const CONTRACT_TYPES = {
  Credit: {
    label: 'Credit',
    drawdown: true,
    pctLabel: '% used',
    pctHint: 'of credit',
    leftLabel: 'Remaining',
    overHint: 'over credit',
  },
  Commitment: {
    label: 'Commitment',
    drawdown: true,
    pctLabel: '% met',
    pctHint: 'of commitment',
    leftLabel: 'Left to meet',
    overHint: 'commitment exceeded',
  },
  Subscription: { label: 'Subscription', drawdown: false },
  PayAsYouGo: { label: 'Pay as you go', drawdown: false },
};
const contractType = (t) => CONTRACT_TYPES[t] || { label: t || 'Contract', drawdown: false };

export default function Billing() {
  const { isAdmin } = useAuth();
  const groupsReq = useAsync(() => api.getUserGroups(), []);
  const groups = useMemo(() => groupsReq.data || [], [groupsReq.data]);

  const [groupId, setGroupId] = useState('');
  const [start, setStart] = useState(defaultStart);
  const [end, setEnd] = useState(todayISO);
  const [applied, setApplied] = useState(() => ({
    groupId: '',
    start: defaultStart(),
    end: todayISO(),
  }));
  const [query, setQuery] = useState('');
  const [spendViews, setSpendViews] = useState({ day: 'service', month: 'service' });

  const recordsReq = useAsync(
    () =>
      api.getUsageRecords({
        groupId: applied.groupId,
        start: applied.start,
        end: applied.end,
      }),
    [applied]
  );

  // The spend chart reads its own endpoint: the stored snapshot bucketed by calendar
  // month, priced at read time against the group's current rates like every other
  // figure on this page. It is deliberately *not* scoped by the date filter — it is
  // the year of context behind the period, and re-cutting it to the selected dates
  // would leave a single column for a short default (last 30 days) period.
  const monthsReq = useAsync(
    () => api.getUsageMonths({ groupId: applied.groupId }),
    [applied.groupId]
  );

  // The day series is its own endpoint over the *other* stored snapshot: the daily
  // rows, which an administrator writes with "Store for client" rather than "Store 12
  // months". It is scoped by the selected dates — unlike the monthly chart above,
  // whose whole job is the context around them — because a day view is only ever
  // asked for with a window in mind.
  //
  const daysReq = useAsync(
    () => api.getUsageDays({
      groupId: applied.groupId,
      start: applied.start,
      end: applied.end,
    }),
    [applied]
  );

  // A Credit contract is prepaid, so the group can see how much of it is left.
  // Other contract types have nothing to draw down, so only billing is shown.
  const groupDetail = useAsync(
    () =>
      applied.groupId
        ? api.getUserGroup(applied.groupId).catch(() => null)
        : Promise.resolve(null),
    [applied]
  );

  const rows = useMemo(() => recordsReq.data?.data || [], [recordsReq.data]);
  const meta = recordsReq.data?.meta || {};

  // Roll the stored per-day rows up to one line per tenant · service · SKU.
  const summary = useMemo(() => {
    const byKey = new Map();
    for (const r of rows) {
      const k = `${r.tenantName}|${r.service}|${r.skuId}|${r.currency}`;
      const e = byKey.get(k);
      if (e) {
        e.quantity += r.quantity || 0;
        e.billing += r.billing || 0;
      } else {
        byKey.set(k, {
          ...r,
          quantity: r.quantity || 0,
          billing: r.billing || 0,
        });
      }
    }
    const q = query.trim().toLowerCase();
    return [...byKey.values()]
      .filter((r) =>
        q
          ? `${r.tenantName} ${r.service} ${r.skuId} ${r.skuName}`
              .toLowerCase()
              .includes(q)
          : true
      )
      .sort((a, b) => b.billing - a.billing);
  }, [rows, query]);

  // Cost is deliberately absent: the API strips the buy side for clients, so
  // there is nothing to total and nothing to show.
  //
  // Billing is only ever added up within one currency — a period can carry rows
  // in more than one, and a single figure adding USD to SGD would mean nothing —
  // so the summary carries one total per currency, the same rule the pivot's
  // Total columns follow.
  const totals = useMemo(() => {
    const fallback = summary[0]?.currency || meta.currency || 'USD';
    const byCurrency = {};
    for (const r of summary) {
      const c = r.currency || fallback;
      const t = (byCurrency[c] = byCurrency[c] || { billing: 0, lines: 0 });
      t.billing += r.billing || 0;
      t.lines += 1;
    }
    const currencies = Object.keys(byCurrency).sort();
    // An empty period still shows one (zeroed) card rather than none.
    if (currencies.length === 0) currencies.push(fallback);
    return {
      currencies,
      // What the pivot falls back to for a row carrying no currency of its own.
      currency: fallback,
      of: (c) => byCurrency[c] || { billing: 0, gross: 0, lines: 0 },
      multi: currencies.length > 1,
    };
  }, [summary, meta.currency]);

  const serviceSummary = useMemo(() => {
    const byCurrency = new Map();
    for (const row of summary) {
      const currency = row.currency || totals.currency;
      const services = byCurrency.get(currency) || new Map();
      const service = row.service || 'Unattributed';
      services.set(service, (services.get(service) || 0) + (row.billing || 0));
      byCurrency.set(currency, services);
    }
    return [...byCurrency.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([currency, services]) => ({
        currency,
        services: [...services.entries()]
          .map(([service, billing]) => ({ service, billing }))
          .sort((a, b) => b.billing - a.billing || a.service.localeCompare(b.service)),
      }));
  }, [summary, totals.currency]);

  // A single-currency period keeps reading "Billing", not "Billing (USD)".
  const perCurrency = (label, c) => (totals.multi ? `${label} (${c})` : label);

  // Every contract the group holds, summarised per type — and per currency
  // within a type, since a contract is held in one and two currencies cannot be
  // added. A Credit is prepaid and a Commitment is a promise to spend, so both
  // are drawn down by what the group is billed and carry a percentage and a
  // balance; a Subscription or a PayAsYouGo contract has no balance to draw
  // down, so it reports its value and its term instead of an invented
  // percentage.
  const contractGroups = useMemo(() => {
    const byKey = new Map();
    for (const c of groupDetail.data?.contracts || []) {
      const currency = c.currency || totals.currency;
      const key = `${c.type}|${currency}`;
      const e = byKey.get(key) || {
        type: c.type,
        currency,
        count: 0,
        value: 0,
        start: null,
        end: null,
      };
      e.count += 1;
      e.value += c.value || 0;
      // Widest term the contracts of this type cover.
      if (c.start && (!e.start || c.start < e.start)) e.start = c.start;
      if (c.end && (!e.end || c.end > e.end)) e.end = c.end;
      byKey.set(key, e);
    }
    return [...byKey.values()].sort(
      (a, b) => a.type.localeCompare(b.type) || a.currency.localeCompare(b.currency)
    );
  }, [groupDetail.data, totals]);

  // One set of views per currency: the four costliest services keep a colour of their
  // own and the tail folds into Other, so each column still stacks to that month's
  // true billing. `total` backs the line view and `services` the table, which carries
  // the folded tail by name.
  const monthsMeta = monthsReq.data?.meta || {};
  const monthAxis = useMemo(
    () => (monthsMeta.axis || []).filter((month) => month >= BILLING_FIRST_MONTH),
    [monthsMeta.axis]
  );
  const monthCharts = useMemo(
    () =>
      monthlyCharts((monthsReq.data?.data || []).filter((row) => row.month >= BILLING_FIRST_MONTH), {
        axis: monthAxis,
        splits: monthsReq.data?.meta?.currencies,
        // Billing, never cost: the endpoint has no cost field to read.
        valueOf: (r) => r.billing,
        fallbackCurrency: totals.currency,
        topN: TOP_SERVICES,
      }),
    [monthsReq.data, monthAxis, totals.currency]
  );

  // The daily series, same shape one bucket finer. Rows key on `date`, which is all
  // monthlyCharts needs told — the ranking, the four-colour fold and the gap rule are
  // grain-agnostic.
  const daysMeta = daysReq.data?.meta || {};
  const dayAxis = useMemo(() => daysMeta.axis || [], [daysMeta.axis]);
  const dayCharts = useMemo(
    () =>
      monthlyCharts(daysReq.data?.data || [], {
        axis: daysMeta.axis || [],
        splits: daysMeta.currencies,
        valueOf: (r) => r.billing,
        bucketOf: (r) => r.date,
        fallbackCurrency: totals.currency,
        topN: TOP_SERVICES,
      }),
    [daysReq.data, daysMeta.axis, daysMeta.currencies, totals.currency]
  );

  // Each chart keeps its own data, date axis, and request state.
  const spends = [
      {
        key: 'day',
        req: daysReq,
        charts: dayCharts,
        axis: dayAxis,
        formatLabel: dayTick,
        formatPoint: dayFull,
        // The days actually stored, not the window that was asked for — the backend
        // trims the axis to the snapshot's reach, and the title has to say the same
        // thing the axis does. Before it lands, the picked dates.
        span:
          dayAxis.length > 0
            ? dayRange(dayAxis[0], dayAxis[dayAxis.length - 1])
            : dayRange(applied.start, applied.end),
        // Both forms of the bucket's name: "per day" and "daily spend". Deriving one
        // from the other gives "dayly".
        noun: 'day',
        adjective: 'daily',
        syncedAt: daysMeta.lastSyncedAt,
      },
      {
        key: 'month',
        req: monthsReq,
        charts: monthCharts,
        axis: monthAxis,
        formatLabel: monthTick,
        formatPoint: monthFull,
        // Empty when nothing is stored, so the title does not claim "last 0 months"
        // over an empty-state that is about to explain the absence.
        span:
          monthAxis.length > 0
            ? `${monthFull(monthAxis[0])} – ${monthFull(monthAxis[monthAxis.length - 1])}`
            : '',
        noun: 'month',
        adjective: 'monthly',
        syncedAt: monthsMeta.lastSyncedAt,
      },
  ];

  const applyFilters = () => setApplied({ groupId, start, end });

  if (groupsReq.loading) return <Loading label="Loading billing…" />;
  if (groupsReq.error) {
    return <ErrorState error={groupsReq.error} onRetry={groupsReq.reload} />;
  }

  return (
    <div className="stack">
      <PageHeader
        title="Billing"
        subtitle="What you are billed, as of the last refresh."
        actions={
          <button
            className="btn btn--ghost"
            onClick={() => { recordsReq.reload(); daysReq.reload(); monthsReq.reload(); }}
            disabled={recordsReq.loading}
          >
            <Icon name={ICONS.refresh} /> Refresh
          </button>
        }
      />

      {groups.length === 0 ? (
        <Card>
          <EmptyState
            title="No user group"
            detail="Billing is reported per user group, and your account is not a member of one yet."
          />
        </Card>
      ) : (
        <>
          <Card title="Period">
            <div className="filter-row">
              <label className="field">
                <span>User group</span>
                <select
                  className="input"
                  value={groupId}
                  onChange={(e) => setGroupId(e.target.value)}
                >
                  <option value="">
                    {isAdmin ? 'All groups' : 'All my groups'}
                  </option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Start date</span>
                <input
                  className="input"
                  type="date"
                  value={start}
                  onChange={(e) => setStart(e.target.value)}
                />
              </label>
              <label className="field">
                <span>End date</span>
                <input
                  className="input"
                  type="date"
                  value={end}
                  onChange={(e) => setEnd(e.target.value)}
                />
              </label>
              <label className="field">
                <span>Filter</span>
                <input
                  className="input"
                  placeholder="service, SKU…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </label>
              <div className="field">
                <span aria-hidden="true">&nbsp;</span>
                <button className="btn btn--primary" onClick={applyFilters}>
                  Show
                </button>
              </div>
            </div>
          </Card>

          {recordsReq.loading ? (
            <Loading label="Loading billing…" />
          ) : recordsReq.error ? (
            <ErrorState error={recordsReq.error} onRetry={recordsReq.reload} />
          ) : (
            <>
              {/* One card per currency; each contract type keeps its own row so
                  none of them ever read as one running total. */}
              <div className="grid grid--kpi">
                {totals.currencies.map((c) => (
                  <StatCard
                    key={`billing-${c}`}
                    label={perCurrency('Billing', c)}
                    value={currencyExact(totals.of(c).billing, c)}
                    hint="amount payable"
                  />
                ))}
                {groupDetail.data?.discount > 0 ? (
                  <StatCard
                    label="Discount"
                    value={`${groupDetail.data.discount}%`}
                    hint="already applied to billing"
                  />
                ) : null}
              </div>

              {contractGroups.map((g) => {
                const t = contractType(g.type);
                const billed = totals.of(g.currency).billing;
                const pct = g.value > 0 ? (billed / g.value) * 100 : null;
                const left = g.value - billed;
                const term =
                  g.start || g.end
                    ? `${fmtDate(g.start)} → ${fmtDate(g.end)}`
                    : 'no term set';
                return (
                  <div className="grid grid--kpi" key={`${g.type}-${g.currency}`}>
                    <StatCard
                      label={`${t.label} value`}
                      value={currencyExact(g.value, g.currency)}
                      hint={`${g.count} contract${g.count === 1 ? '' : 's'} · ${term}`}
                    />
                    {t.drawdown ? (
                      <>
                        <StatCard
                          label={t.pctLabel}
                          value={pct != null ? `${pct.toFixed(1)}%` : '—'}
                          hint={
                            totals.multi
                              ? `${t.pctHint} · ${g.currency} billing only`
                              : t.pctHint
                          }
                        />
                        <StatCard
                          label={t.leftLabel}
                          value={currencyExact(left, g.currency)}
                          hint={left < 0 ? t.overHint : 'left'}
                        />
                      </>
                    ) : (
                      // Nothing is drawn down, so the term is what there is to
                      // say beyond the value.
                      <StatCard label="Term" value={term} hint={`${t.label} contract`} />
                    )}
                  </div>
                );
              })}

              {spends.map((spend) => {
                const byDay = spend.key === 'day';
                const spendView = spendViews[spend.key];
                return (
                <Card
                  key={spend.key}
                  className="card--quiet-title"
                  // The heading names what is on screen: the views show different
                  // things, not one thing relabelled — and the grain is part of that,
                  // since "Monthly" and "Daily" are different claims about a column.
                  title={`${titleCase(spend.adjective)} spend${
                    spendView === 'total' ? '' : ' by service'
                  }${spend.span ? ` — ${spend.span}` : ''}`}
                  action={
                    <div className="usage-export">
                      <div
                        className="seg"
                        role="tablist"
                        aria-label={`How ${spend.adjective} spend is shown`}
                      >
                        {[
                          ['service', 'By service'],
                          ['total', 'Total'],
                          ['table', 'Table'],
                        ].map(([view, label]) => (
                          <button
                            key={view}
                            type="button"
                            role="tab"
                            className={`seg__btn ${spendView === view ? 'seg__btn--on' : ''}`}
                            onClick={() => setSpendViews((current) => ({ ...current, [spend.key]: view }))}
                            aria-selected={spendView === view}
                            title={
                              view === 'total'
                                ? `The ${spend.noun} totals as a line: is your bill rising?`
                                : view === 'service'
                                  ? `Stacked columns: what each ${spend.noun} is made of`
                                  : `Every service and ${spend.noun} as figures`
                            }
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                      {spend.syncedAt ? (
                        <span className="card__meta">
                          as of {fmtDate(spend.syncedAt)}
                        </span>
                      ) : null}
                    </div>
                  }
                >
                  {spend.req.loading ? (
                    <Loading label={`Loading ${spend.adjective} spend…`} />
                  ) : spend.req.error ? (
                    <ErrorState error={spend.req.error} onRetry={spend.req.reload} />
                  ) : spend.charts.length === 0 ? (
                    <EmptyState
                      title={`Nothing stored ${byDay ? 'per day' : 'per month'}`}
                      detail={
                        byDay
                          ? 'No daily billing is stored inside the selected period. Try a wider period, or ask an administrator to refresh it.'
                          : 'No monthly billing is stored from August 2026 onward — ask an administrator to refresh it.'
                      }
                    />
                  ) : spendView === 'table' ? (
                    <div className="chart-multiples">
                      {spend.charts.map((c) => (
                        <div key={c.currency}>
                          {spend.charts.length > 1 ? (
                            <h3 className="chart-multiples__title">{c.label}</h3>
                          ) : null}
                          <div className="table-wrap">
                            {/* Service columns, ordered by what each one bills over the
                                whole snapshot, with the bucket total last. Every service
                                is here — the chart's top-four fold is a limit of colour,
                                not of the data. */}
                            <table className="table table--dense table--pivot">
                              <thead>
                                <tr>
                                  <th>{byDay ? 'Day' : 'Month'}</th>
                                  {c.services.map((s) => (
                                    <th key={s.key} className="num">{s.label}</th>
                                  ))}
                                  <th className="num">Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {spend.axis.map((m, i) => (
                                  <tr key={m}>
                                    <td className="cell-primary">{spend.formatPoint(m)}</td>
                                    {c.services.map((s) => (
                                      <td key={s.key} className="num">
                                        {/* A dash, not a zero: nothing was stored here. */}
                                        {s.values[i] == null
                                          ? '—'
                                          : currencyExact(s.values[i], c.code)}
                                      </td>
                                    ))}
                                    <td className="num cell-total">
                                      {c.total.values[i] == null
                                        ? '—'
                                        : currencyExact(c.total.values[i], c.code)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr>
                                  <td className="cell-primary">Total</td>
                                  {c.services.map((s) => (
                                    <td key={s.key} className="num cell-total">
                                      {currencyExact(s.total, c.code)}
                                    </td>
                                  ))}
                                  <td className="num cell-total">
                                    {currencyExact(c.total.total, c.code)}
                                  </td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="chart-multiples">
                      {spend.charts.map((c) => (
                        <div key={c.currency}>
                          {spend.charts.length > 1 ? (
                            <h3 className="chart-multiples__title">{c.label}</h3>
                          ) : null}
                          {/* Two forms for two questions, never one chart doing both:
                              the breakdown stacks to each bucket's total, so drawing the
                              total as a fifth line beside its own parts would dwarf
                              every one of them. */}
                          {spendView === 'service' ? (
                            <StackedBars
                              labels={spend.axis}
                              series={c.chartServices}
                              height={280}
                              formatValue={(v) => currencyExact(v, c.code)}
                              formatTick={(v) => currencyCompact(v, c.code)}
                              formatLabel={spend.formatLabel}
                              formatPoint={spend.formatPoint}
                              ariaLabel={`${c.code} billing by service per ${spend.noun}, ${spend.span}`}
                            />
                          ) : (
                            <LineChart
                              labels={spend.axis}
                              series={[c.total]}
                              height={260}
                              formatValue={(v) => currencyExact(v, c.code)}
                              formatTick={(v) => currencyCompact(v, c.code)}
                              formatLabel={spend.formatLabel}
                              formatPoint={spend.formatPoint}
                              ariaLabel={`${c.code} billing per ${spend.noun}, ${spend.span}`}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="field__hint">
                    What you are billed per calendar {spend.noun}, by service, at your
                    current rates. The last {spend.noun} is still accumulating, and a{' '}
                    {spend.noun} with nothing stored is left as a gap rather than shown
                    as zero.
                    {byDay
                      ? ' Days come from your stored line items, so this chart reaches only as far as they do — switch to Month for the year behind it.'
                      : ' Monthly billing is shown from August 2026 onward. Months with no stored data stay as gaps.'}
                    {spendView === 'service'
                      ? ' Only the four costliest services get a colour of their own — the rest are folded into Other, and Table lists every one.'
                      : ''}
                  </p>
                </Card>
                );
              })}

              <Card
                className="card--quiet-title"
                title="Billing summary by service"
                action={
                  <div className="usage-export">
                    <span className="card__meta">{dayRange(applied.start, applied.end)}</span>
                    <button
                      className="btn btn--ghost btn--sm"
                      onClick={() =>
                        downloadCsv(
                          lineItemsCsv(rows),
                          `billing-prices-${applied.start}_${applied.end}.csv`
                        )
                      }
                      disabled={rows.length === 0}
                      title="Download every line item with its billing price as CSV (';' separated)"
                    >
                      ⭳ Billing price CSV
                    </button>
                  </div>
                }
              >
                {serviceSummary.length === 0 ? (
                  <EmptyState
                    title="No services for this period"
                    detail="No stored billing matches the selected dates and filter."
                  />
                ) : (
                  <div className="chart-multiples">
                    {serviceSummary.map(({ currency, services }) => (
                      <div key={currency}>
                        <h3 className="chart-multiples__title">{currency}</h3>
                        <div className="table-wrap">
                          <table className="table table--dense">
                            <thead>
                              <tr>
                                <th scope="col">Service</th>
                                <th scope="col" className="num">Billing ({currency})</th>
                              </tr>
                            </thead>
                            <tbody>
                              {services.map(({ service, billing }) => (
                                <tr key={service}>
                                  <td className="cell-primary">{service}</td>
                                  <td className="num">{currencyExact(billing, currency)}</td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr>
                                <th scope="row">Total</th>
                                <td className="num cell-total">
                                  {currencyExact(totals.of(currency).billing, currency)}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {/* Title matches the heading the pivot's default (Billing) tab
                  uses, so the card does not rename itself once rows arrive. */}
              {summary.length === 0 ? (
                <Card className="card--quiet-title" title="Billing by date × service · SKU">
                  <EmptyState
                    title="Nothing billed for this period"
                    detail={
                      meta.lastSyncedAt
                        ? 'No stored usage falls in the selected dates.'
                        : 'Usage has not been pulled for this group yet — ask an administrator to refresh it.'
                    }
                  />
                </Card>
              ) : (
                <UsagePivot
                  rows={rows}
                  metrics={['billing', 'usage']}
                  fallbackCurrency={totals.currency}
                  fileBase={`billing-${applied.start}_${applied.end}`}
                />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
